/**
 * Shared contract between ocean/booking-bar (dates/guests/location),
 * ocean/property-results (the results/map display), and ocean/booking-filters
 * (amenities/collection/bedrooms/bathrooms, in a sidebar) -- three
 * independent blocks that can sit anywhere in a template, with unrelated
 * blocks between them, or not all appear together. They agree on two
 * distinct layers plus a notification channel:
 *
 * 1. Server-side context (window.oceanSearchData) -- injected once per page
 *    load by inject_ocean_search_data() in php/search-support.php, and never
 *    read from or written to the URL. Two kinds:
 *      - Reference data: `locations`/`collections`/`amenities` lists, for
 *        populating pickers.
 *      - `lockedScope`: which single term (if any) the *page itself* is
 *        scoped to (a property-location or property-collection archive).
 *        Authoritative -- always wins over any client-side filter value for
 *        that taxonomy.
 * 2. Filters -- the user-adjustable inputs (dates, guests, location when not
 *    locked, pets, amenities, collection, min bedrooms/bathrooms). These are
 *    represented as URL query params so they're shareable/reload-safe and so
 *    blocks can read each other's current filter state without direct
 *    coupling. `applyLockedScope()` is the seam: it forces the locked
 *    taxonomy's value over a filter param set.
 * 3. A live-update channel -- whichever block changes a filter pushes the
 *    new params onto the URL and dispatches OCEAN_SEARCH_EVENT on
 *    `document`; property-results listens for it and re-fetches. Neither
 *    block needs to know which others are on the page.
 */

export interface OceanLockedScope
	{
	taxonomy: 'property-location' | 'property-collection';
	term: string;
	label: string;
	}

export interface OceanSearchData
	{
	locations: Array<{ value: string; label: string }>;
	collections: Array<{ value: string; label: string }>;
	// Every property-feature term. `pinned` (the "Pin above collapsible
	// list" ACF field) is used by booking-filters to decide what shows
	// directly under the bedroom/bathroom steppers vs. inside its
	// collapsible "Property features" section. `shortName` (the "Short
	// name" ACF field) is a compact label like "For pets" -- booking-bar's
	// individual feature-checkbox field-blocks use it in place of the full
	// label where set, to save horizontal space. Each booking-bar feature
	// field-block picks a single term from this list via its own
	// `featureSlug` attribute -- there's no separate filtered subset any
	// more (the old `amenities`/show_in_search split).
	features: Array<{ value: string; label: string; icon?: string | null; caveat?: string | null; pinned?: boolean; shortName?: string | null }>;
	lockedScope: OceanLockedScope | null;
	// Deep link from an ocean/collections card on another page (e.g.
	// /find-a-stay/beach-front/ -- see ocean_register_collection_rewrite_rule()
	// in php/ocean-property-collections.php). Only ever set on the search-
	// results page itself. Unlike lockedScope this is just the *initial*
	// value of the normal `collection` filter, not a forced/locked one --
	// see getInitialCollectionSlug() below.
	initialCollection: string;
	// True only when the current page is the one picked as `search_results_page`
	// in Site Options -- ocean/collections' run.tsx uses this to decide
	// whether a card click should filter in place (already on that page) or
	// navigate there (placed on any other page). ocean/booking-bar uses the
	// same flag (via isOnSearchResultsPage() below) for the same decision.
	isSearchResultsPage: boolean;
	// The `search_results_page` Site Options URL itself -- used by
	// ocean/booking-bar to navigate there when it's *not* already on that
	// page (see isOnSearchResultsPage() below).
	searchResultsPageUrl: string;
	ajaxUrl: string;
	hostawayClientId?: string;
	googleMapsApiKey?: string;
	}

declare global
	{
	interface Window
		{
		oceanSearchData?: OceanSearchData;
		}
	}

export const OCEAN_SEARCH_EVENT = 'ocean:search';

export interface OceanSearchParams
	{
	location: string;
	check_in: string;
	check_out: string;
	guests: string;
	pets: string;
	date_flex: string;
	amenities: string;
	collection: string;
	min_bedrooms: string;
	min_bathrooms: string;
	sort: string;
	}

export const getQueryParam = (name: string): string =>
	{
	const params = new URLSearchParams(window.location.search);
	return params.get(name) || '';
	};

/**
 * The active collection filter -- the `collection` query param when present
 * (a live update, or a reload/bookmark of one), otherwise the pretty-path
 * deep link server-injected as window.oceanSearchData.initialCollection (see
 * OceanSearchData above). Shared by getSearchParamsFromUrl() below and
 * collections/src/run.tsx's own deep-link restore, so both single sources
 * agree on where this value comes from.
 */
export const getInitialCollectionSlug = (): string =>
	getQueryParam('collection') || window.oceanSearchData?.initialCollection || '';

/**
 * Whether the current page is the one picked as `search_results_page` in
 * Site Options -- the shared "filter in place vs. navigate there" check,
 * used by ocean/collections' run.tsx and ocean/booking-bar's BookingBarForm.
 */
export const isOnSearchResultsPage = (): boolean =>
	Boolean(window.oceanSearchData?.isSearchResultsPage);

/**
 * Reads the current search params from the URL, so a page load (direct
 * link, bookmark, reload) reconstructs the same search a live update would
 * have produced.
 */
export const getSearchParamsFromUrl = (): Partial<OceanSearchParams> =>
	({
	location: getQueryParam('location'),
	check_in: getQueryParam('check_in'),
	check_out: getQueryParam('check_out'),
	guests: getQueryParam('guests'),
	pets: getQueryParam('pets'),
	date_flex: getQueryParam('date_flex'),
	amenities: getQueryParam('amenities'),
	collection: getInitialCollectionSlug(),
	min_bedrooms: getQueryParam('min_bedrooms'),
	min_bathrooms: getQueryParam('min_bathrooms'),
	sort: getQueryParam('sort'),
	});

/**
 * Applies the page's lockedScope (if any) over a param set -- the locked
 * taxonomy's field always wins, since it isn't user-editable.
 */
export const applyLockedScope = (params: Partial<OceanSearchParams>, lockedScope: OceanLockedScope | null | undefined): Partial<OceanSearchParams> =>
	{
	if (!lockedScope)
		{
		return params;
		}

	if (lockedScope.taxonomy === 'property-location')
		{
		return { ...params, location: lockedScope.term };
		}

	return { ...params, collection: lockedScope.term };
	};

/**
 * Pushes the given params onto the URL (shareable/reloadable state) and
 * notifies any property-results block listening on this page. Booking-bar
 * calls this instead of fetching results itself.
 */
export const dispatchOceanSearch = (params: Partial<OceanSearchParams>): void =>
	{
	const url = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value)
			{
			url.set(key, value);
			}
		});

	const query = url.toString();
	const newUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
	window.history.pushState(null, '', newUrl);

	document.dispatchEvent(new CustomEvent<Partial<OceanSearchParams>>(OCEAN_SEARCH_EVENT, { detail: params }));
	};

/**
 * property-results' "Filter" link doesn't own a filter sidebar itself -- it
 * asks whichever ocean/booking-filters block is on the page to open its own,
 * the same loosely-coupled way OCEAN_SEARCH_EVENT works.
 */
export const OCEAN_OPEN_FILTERS_EVENT = 'ocean:open-filters';

export const dispatchOpenFilters = (): void =>
	{
	document.dispatchEvent(new CustomEvent(OCEAN_OPEN_FILTERS_EVENT));
	};
